import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ScrollReveal } from '@/components/ui/scroll-reveal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowRight, Loader2 } from 'lucide-react'
import { getBusinessSectors, BusinessSector } from '@/services/content'
import { iconMap } from '@/lib/icons'
import useLanguageStore from '@/stores/languageStore'

export const BusinessAreasSection = () => {
  const { currentLocale } = useLanguageStore()
  const [sectors, setSectors] = useState<BusinessSector[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSectors = async () => {
      setLoading(true)
      const { data } = await getBusinessSectors(currentLocale)
      if (data) setSectors(data)
      setLoading(false)
    }
    fetchSectors()
  }, [currentLocale])

  const scrollToCta = () => {
    const element = document.getElementById('cta')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section id="business-areas" className="py-24 bg-slate-50">
      <div className="container px-4 md:px-6">
        <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Soluções Adaptadas ao Seu Setor
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            {currentLocale === 'pt'
              ? 'A nossa tecnologia é flexível e molda-se às necessidades específicas de diferentes verticais de negócio.'
              : 'Our technology is flexible and shapes itself to the specific needs of different business verticals.'}
          </p>
        </ScrollReveal>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {sectors.map((area, index) => {
              const Icon = iconMap[area.icon_name] || iconMap['Globe']
              const cardContent = (
                <Card className="h-full flex flex-col bg-white border-slate-200 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1 group overflow-hidden cursor-pointer">
                  {area.image_url ? (
                    <div className="w-full h-48 overflow-hidden bg-slate-100 relative">
                      <img
                        src={area.image_url}
                        alt={area.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-slate-50 flex items-center justify-center border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                      <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                        <Icon className="w-8 h-8 text-primary/80" />
                      </div>
                    </div>
                  )}

                  <div className="flex-1 flex flex-col p-6">
                    <CardHeader className="p-0 mb-4 space-y-0">
                      <CardTitle className="text-xl font-bold text-slate-900 group-hover:text-primary transition-colors flex items-center gap-2">
                        {area.title}
                        <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 mt-auto">
                      <div
                        className="text-slate-600 text-sm leading-relaxed prose prose-sm prose-slate max-w-none line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: area.description }}
                      />
                    </CardContent>
                  </div>
                </Card>
              )

              return (
                <ScrollReveal key={area.id} delay={index * 100}>
                  {area.slug ? (
                    <Link to={`/setores/${area.slug}`} className="block h-full">
                      {cardContent}
                    </Link>
                  ) : (
                    cardContent
                  )}
                </ScrollReveal>
              )
            })}
          </div>
        )}

        <ScrollReveal delay={400} className="text-center">
          <Button
            variant="link"
            onClick={scrollToCta}
            className="text-primary font-semibold text-lg hover:underline decoration-2 underline-offset-4 p-0 h-auto gap-2"
          >
            {currentLocale === 'pt'
              ? 'Ver como aplicamos ao seu caso'
              : 'See how we apply it to your case'}
            <ArrowRight className="w-5 h-5" />
          </Button>
        </ScrollReveal>
      </div>
    </section>
  )
}
